import React from 'react';
import PropTypes from 'prop-types';

import ProviderRosterTableRow from './ProviderRosterTableRow';
import { getNextSortValue } from './sorting';

/**
 * @typedef {import('../types').RosterProvider} RosterProvider
 */

/**
 * @param {Object} props
 * @param {RosterProvider[]} props.providers - Array of providers
 * @param {(providerCode: string, updates: Partial<RosterProvider>) => void} props.onProviderChange - Callback to update a provider by merging partial updates
 * @param {(providerId: string) => void} props.onRemove - Remove provider handler
 * @param {string} props.sortBy - Current sort value
 * @param {(sortValue: string) => void} props.onSortChange - Sort change handler
 * @param {boolean} [props.disabled = false] - Whether the table is disabled
 */
const ProviderRosterTable = ({
  providers,
  onProviderChange,
  onRemove,
  sortBy,
  onSortChange,
  disabled = false,
}) => {
  /**
   * Handle clicking on a sortable column header
   * Uses getNextSortValue from constants to determine the next sort state
   * @param {string} columnKey - The column to sort by ('provider-name' or 'practice')
   */
  const handleHeaderClick = (columnKey) => {
    const nextSortValue = getNextSortValue(columnKey, sortBy);
    onSortChange(nextSortValue);
  };

  /**
   * Get the rotation class for the chevron icon based on current sort
   * @param {string} columnKey - The column key
   * @returns {string} - The CSS class for rotation
   */
  const getChevronRotation = (columnKey) => {
    if (sortBy === `${columnKey}-asc`) {
      return 'rotate-180';
    }
    return '';
  };

  /**
   * Check if a column is currently being sorted
   * @param {string} columnKey - The column key
   * @returns {boolean}
   */
  const isActiveSort = (columnKey) => {
    return sortBy === `${columnKey}-asc` || sortBy === `${columnKey}-desc`;
  };

  return (
    <table className="hidden md:table" data-testid="provider-roster-table">
      <thead>
        <tr className="flex items-center gap-8 self-stretch rounded-t-md border text-deep-blue-70 border-gray-20 border-b-0 bg-soft-blue px-4 py-5 text-left">
          <th className="w-[30%]">
            <button
              type="button"
              className="flex items-center gap-3 w-full cursor-pointer hover:opacity-80 bg-transparent border-0 p-0 text-left"
              onClick={() => handleHeaderClick('provider-name')}
              aria-label="Sort by provider name"
            >
              <span
                className={`text-sm ${isActiveSort('provider-name') ? 'font-bold' : ''}`}
              >
                Provider Name
              </span>
              {isActiveSort('provider-name') && (
                <img
                  src="/public/images/chevron-down.svg"
                  alt="chevron-down"
                  className={`w-4 h-4 transition-transform ${getChevronRotation('provider-name')}`}
                />
              )}
            </button>
          </th>
          <th className="w-[30%]">
            <button
              type="button"
              className="flex items-center gap-3 w-full cursor-pointer hover:opacity-80 bg-transparent border-0 p-0 text-left"
              onClick={() => handleHeaderClick('practice')}
              aria-label="Sort by practice"
            >
              <span
                className={`text-sm ${isActiveSort('practice') ? 'font-bold' : ''}`}
              >
                Primary Practice/Office Location
              </span>
              {isActiveSort('practice') && (
                <img
                  src="/public/images/chevron-down.svg"
                  alt="chevron-down"
                  className={`w-4 h-4 transition-transform ${getChevronRotation('practice')}`}
                />
              )}
            </button>
          </th>
          <th className="flex-1">
            <span className="text-sm">Email Address</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {providers.map((provider, index) => (
          <ProviderRosterTableRow
            key={provider.providerCode || index}
            provider={provider}
            isEven={index % 2 === 0}
            onChange={onProviderChange}
            onRemove={onRemove}
            disabled={disabled}
            existingProviders={providers}
          />
        ))}
      </tbody>
    </table>
  );
};

ProviderRosterTable.propTypes = {
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      providerCode: PropTypes.string,
      displayName: PropTypes.string,
      npi: PropTypes.string,
      providerImageUrl: PropTypes.string,
      primarySpecialty: PropTypes.shape({
        specialtyName: PropTypes.string,
        specialtyCode: PropTypes.string,
      }),
      office: PropTypes.shape({
        name: PropTypes.string,
        addressLine: PropTypes.string,
        cityState: PropTypes.string,
        phone: PropTypes.string,
        fax: PropTypes.string,
      }),
      email: PropTypes.string,
    }),
  ).isRequired,
  onProviderChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  onSortChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ProviderRosterTable;
